import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowDownToLine, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";

const EASE = [0.76, 0, 0.24, 1];

/** Gated download — email in exchange for the GCC HR Readiness Checklist PDF. */
export const ChecklistGate = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const r = await api
        .post("/lead-magnet/checklist", { email: email.trim(), name: name.trim() || undefined })
        .then((res) => res.data);
      setStatus("success");
      // Trigger the download
      const a = document.createElement("a");
      a.href = r.download;
      a.download = "orphion-gcc-hr-readiness-checklist.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="py-16 md:py-24 border-b border-[var(--line-strong)] bg-[var(--bg-2)]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="md:col-span-6"
        >
          <p className="overline mb-4">Free download</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] leading-[1.0] text-[var(--ink)] mb-5">
            The GCC HR{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              Readiness Checklist
            </span>
            .
          </h2>
          <p className="text-[var(--ink-80)] text-sm md:text-base leading-relaxed max-w-lg">
            Twenty boxes to tick before your first fifty hires — the same checklist
            we run in week one of a GCC Launch Sprint. Entity &amp; compliance,
            hiring engine, employer brand, governance. Two pages, no fluff.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="md:col-span-6"
          data-testid="checklist-gate"
        >
          {status === "success" ? (
            <div
              className="border border-[var(--accent)]/50 bg-[var(--bg)] px-6 py-6 flex items-start gap-4"
              data-testid="checklist-success"
            >
              <Check className="w-5 h-5 mt-0.5 text-[var(--accent)] shrink-0" strokeWidth={2} />
              <div>
                <p className="font-display text-xl text-[var(--ink)] mb-1">Downloading now.</p>
                <p className="text-sm text-[var(--ink-70)] leading-relaxed">
                  Your checklist is on its way to your downloads folder.{" "}
                  <a
                    href="/downloads/gcc-hr-readiness-checklist.pdf"
                    download
                    className="text-[var(--accent)] underline underline-offset-4"
                    data-testid="checklist-redownload"
                  >
                    Download again
                  </a>
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="border border-[var(--line-strong)] bg-[var(--bg)] p-7 md:p-8 space-y-6" data-testid="checklist-form">
              <div>
                <label className="overline block mb-2">Work email *</label>
                <input
                  required
                  type="email"
                  className="editorial-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  data-testid="checklist-email"
                />
              </div>
              <div>
                <label className="overline block mb-2">Name (optional)</label>
                <input
                  className="editorial-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  data-testid="checklist-name"
                />
              </div>
              {status === "error" && (
                <p className="text-red-600 text-sm" data-testid="checklist-error">
                  That didn't work — please check the email and try again.
                </p>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-pill disabled:opacity-60"
                data-testid="checklist-submit"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Preparing…</span>
                  </>
                ) : (
                  <>
                    <span>Get the checklist</span>
                    <ArrowDownToLine className="w-4 h-4" strokeWidth={1.6} />
                  </>
                )}
              </button>
              <p className="text-xs text-[var(--ink-50)] leading-relaxed">
                We'll only email you if a new GCC resource comes out. Unsubscribe anytime.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ChecklistGate;
